export const ROOT_FOLDER = "Root";
export const PRODUCTS_FOLDER = "Products";
export const CONCIERGE_FOLDER = "Concierge";

export const IMAGE_EXTENSIONS = [".gif", ".jpg", ".jpeg", ".png", ".webp"];
export const DOCUMENT_EXTENSIONS = [".pdf", ".xlsx", ".xls"];
const SPREADSHEET_EXTENSIONS = [".xlsx", ".xls", ".csv"];

export type BlobFileType = "image" | "document";

/**
 * Determine if a file is an image or document based on extension
 */
export function getFileType(filename: string): BlobFileType {
  const ext = filename.substring(filename.lastIndexOf(".")).toLowerCase();
  return DOCUMENT_EXTENSIONS.includes(ext) ? "document" : "image";
}

export type DocumentKind = "pdf" | "spreadsheet" | "file";

/**
 * Which icon a stored non-image file gets. Anything not named here is a plain file, so a new
 * document format shows as itself rather than as a PDF.
 */
export function getDocumentKind(filename: string): DocumentKind {
  const ext = filename.substring(filename.lastIndexOf(".")).toLowerCase();
  if (ext === ".pdf") return "pdf";
  return SPREADSHEET_EXTENSIONS.includes(ext) ? "spreadsheet" : "file";
}

// export type ImageConfig = {
//   imageStorageUrl: string;
// };

export type ImageInfo = {
  p: string;
  l: number;
  m?: string;
  t?: BlobFileType; // Type: "image" or "document" from backend
};

export type ImageDbRaw = {
  //config: ImageConfig;
  images: ImageInfo[];
};

export type BlobImage = {
  name: string;
  folder: string;
  path: string;
  thumb: string;
  size: number;
  modified: string;
  ext: string;
  url: string;
  type?: BlobFileType; // "image" or "document" - set by backend
};

export type ImageFolder = {
  files: BlobImage[];
  size: number;
};

export type ImageDb = {
  //  config: ImageConfig;
  storageUrl: string;
  imageMap: Map<string, BlobImage>;
  folderMap: Map<string, ImageFolder>;
  size: number;
  version: number;
};

export type ImageSelectOptions = {
  path?: string;
  folder?: string;
  multi?: boolean;
  canChange?: boolean;
  canSelectFolder?: boolean;
  onSelect?: (path: string, url: string) => void;
};

export interface FileUpload {
  id: string;
  file?: File;
  preview?: string;
  path?: string;
}

/**
 * S72 - the account's image cache token, held module-level because every consumer reaches image
 * URLs through `parseImagePath(storageUrl, path)`, which has nowhere to thread a third argument.
 * It is a per-session constant, so a module variable is the honest shape rather than a shortcut.
 *
 * 🔑 The token's ONLY contract is that it differs when the bytes differ. It is not a hash and it
 * does not have to match what the server stamped into a published catalog - caches key on the
 * whole URL, so any changed value is a URL nothing has cached, which is the entire mechanism.
 */
let imageVersion: string | undefined;

/** Set from the account config on session load. See `AccountConfig.imageVersion`. */
export const setImageVersion = (version?: string) => {
  imageVersion = version;
};

/**
 * Bump after the client itself changes an image, so the new picture shows immediately without
 * waiting for the next session load. Any increasing value works (see the contract above).
 */
export const bumpImageVersion = () => {
  imageVersion = String(Date.now() * 10000);
};

/**
 * S72 - this used to append `?${Date.now()}` on EVERY render. That was a guaranteed cache miss on
 * every image on every page, and far worse: it made the app permanently fresh while the published
 * catalog served a cached copy, so the owner's screen disagreed with what their customers saw and
 * nothing in the product ever said so. It is the reason stale images went unreported for months.
 *
 * Pass `versioned = false` for a URL that must stay bare (an href a user may copy).
 */
export const getImageUrl = (
  storageUrl: string,
  path: string,
  versioned = true
) =>
  storageUrl + "/" + path + (versioned && imageVersion ? "?v=" + imageVersion : "");

export const parseImagePath = (storageUrl: string, path: string) => {
  if (!path) return null;
  const i = path.lastIndexOf("/");
  const folder = i > 0 ? path.substring(0, i) : "";
  const name = i > 0 ? path.substring(i + 1) : path;
  const e = path.lastIndexOf(".");
  const ext = e > 0 ? path.substring(e + 1) : "";
  return {
    thumb: getImageUrl(storageUrl, combinePath(folder, "_thumb/" + name)),
    url: getImageUrl(storageUrl, path),
    folder,
    name,
    ext,
  };
};

const THUMB_SEGMENT = "/_thumb/";

/**
 * Upgrade a 150px thumb URL to the 400px variant for hover previews.
 * The 400 thumb only exists when the source image was >= 400px
 * (ImageManager.ScaleImageAsync), so callers must fall back to the original
 * URL via `onError` rather than probing the server for existence.
 */
export const getPreviewImageUrl = (thumbUrl: string) => {
  if (!thumbUrl) return thumbUrl;
  const q = thumbUrl.indexOf("?");
  const path = q >= 0 ? thumbUrl.substring(0, q) : thumbUrl;
  const query = q >= 0 ? thumbUrl.substring(q) : "";
  const i = path.lastIndexOf(THUMB_SEGMENT);
  if (i < 0) return thumbUrl;

  const folder = path.substring(0, i + THUMB_SEGMENT.length);
  const name = path.substring(i + THUMB_SEGMENT.length);
  if (!name || name.startsWith("400_") || name.startsWith("700_"))
    return thumbUrl;

  return folder + "400_" + name + query;
};

export const combinePath = (folder, name) => {
  const normalizedFolder = folder || "/";
  return normalizedFolder === "/" ? name : normalizedFolder + "/" + name;
};
